import React, { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Link as LinkIcon, Camera, Gift, Copy, Check, SignOut, Timer } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RaffleDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [stats, setStats] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [postLink, setPostLink] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [applyingRef, setApplyingRef] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Countdown timer
  useEffect(() => {
    const target = new Date("2026-07-01T00:00:00Z");
    const interval = setInterval(() => {
      const now = new Date();
      const diff = target - now;
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [entriesRes, statsRes, referralRes] = await Promise.all([
        axios.get(`${API}/raffle/my-entries`, { withCredentials: true }),
        axios.get(`${API}/raffle/stats`),
        axios.get(`${API}/raffle/my-referral`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data.entries);
      setTotalEntries(entriesRes.data.total_entries);
      setStats(statsRes.data);
      setReferralInfo(referralRes.data);
    } catch (err) {
      console.error("Failed to fetch raffle data:", err);
    }
  };

  const handleSocialClaim = async () => {
    if (!postLink.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/raffle/social-claim`, { post_link: postLink }, { withCredentials: true });
      toast.success("Social media claim submitted! +2 entries pending review.");
      setPostLink("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit claim");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    setApplyingRef(true);
    try {
      const res = await axios.post(`${API}/raffle/apply-referral`, { referral_code: referralCode }, { withCredentials: true });
      toast.success(res.data.message);
      setReferralCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid referral code");
    } finally {
      setApplyingRef(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}?ref=${referralInfo?.referral_code}`;
    // Fallback for when Clipboard API is blocked
    try {
      const textArea = document.createElement("textarea");
      textArea.value = link;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Couldn't copy — long press the link to copy manually");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" style={{ backgroundColor: '#0C0C0C' }} data-testid="raffle-dashboard">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-10"
        >
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>
              Your Raffle
            </h1>
            <p className="font-body text-sm mt-1" style={{ color: '#9A9A9A' }}>
              Welcome, {user.name || user.email}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 border text-sm font-accent transition-colors hover:border-[#FF5A36] hover:text-[#FF5A36]"
            style={{ borderColor: '#2A2A2A', color: '#9A9A9A' }}
            data-testid="logout-btn"
          >
            <SignOut size={16} />
            Logout
          </button>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10"
        >
          <div className="p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <Trophy size={24} style={{ color: '#FF5A36' }} className="mb-3" />
            <p className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>{totalEntries}</p>
            <p className="font-body text-xs mt-1" style={{ color: '#9A9A9A' }}>Your Entries</p>
          </div>
          <div className="p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <Gift size={24} style={{ color: '#A8C6C3' }} className="mb-3" />
            <p className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>{stats?.total_entries || 0}/{RAFFLE_MAX}</p>
            <p className="font-body text-xs mt-1" style={{ color: '#9A9A9A' }}>Total Pool Entries</p>
          </div>
          <div className="p-6 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
            <LinkIcon size={24} style={{ color: '#22c55e' }} className="mb-3" />
            <p className="font-heading text-3xl font-light" style={{ color: '#F0EDE5' }}>{referralInfo?.referral_count || 0}</p>
            <p className="font-body text-xs mt-1" style={{ color: '#9A9A9A' }}>Referrals</p>
          </div>
        </motion.div>

        {/* Raffle Promo Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-8 overflow-hidden border"
          style={{ borderColor: '#FF5A36' }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_exfoliating-bars/artifacts/f2pk75bq_file_00000000a20071fba292d3c7f8d17d39.png"
            alt="Win Free Soap for a Year - Every bar = 1 raffle entry"
            className="w-full object-contain"
            data-testid="raffle-promo-image"
          />
        </motion.div>

        {/* Countdown Timer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="mb-8 p-6 border text-center"
          style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
          data-testid="countdown-timer"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Timer size={20} style={{ color: '#FF5A36' }} />
            <p className="font-accent text-xs tracking-[0.2em] uppercase" style={{ color: '#FF5A36' }}>
              Drawing Countdown
            </p>
          </div>
          <div className="grid grid-cols-4 gap-4 max-w-sm mx-auto">
            <div>
              <p className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>{countdown.days}</p>
              <p className="font-accent text-xs uppercase" style={{ color: '#666' }}>Days</p>
            </div>
            <div>
              <p className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>{countdown.hours}</p>
              <p className="font-accent text-xs uppercase" style={{ color: '#666' }}>Hours</p>
            </div>
            <div>
              <p className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>{countdown.minutes}</p>
              <p className="font-accent text-xs uppercase" style={{ color: '#666' }}>Min</p>
            </div>
            <div>
              <p className="font-heading text-3xl sm:text-4xl font-light" style={{ color: '#F0EDE5' }}>{countdown.seconds}</p>
              <p className="font-accent text-xs uppercase" style={{ color: '#666' }}>Sec</p>
            </div>
          </div>
          <p className="font-body text-xs mt-4" style={{ color: '#9A9A9A' }}>
            Drawing on July 1st, 2026 or when we hit 500 entries — whichever comes first!
          </p>
        </motion.div>

        {/* Prize Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="p-6 border mb-8"
          style={{ borderColor: '#FF5A36', backgroundColor: '#1A1210' }}
        >
          <h3 className="font-heading text-xl font-light mb-2" style={{ color: '#F0EDE5' }}>
            Win Free Soap for a Year!
          </h3>
          <p className="font-body text-sm" style={{ color: '#9A9A9A' }}>
            Drawing on <span style={{ color: '#FF5A36' }}>July 1st, 2026</span> or when we hit 500 entries (whichever comes first).
            Every bar purchased = 1 entry. Post with #dermasandsoap = 2 entries. Refer a friend = 3 entries each!
          </p>
        </motion.div>

        {/* Share Referral Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 border mb-6"
          style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
        >
          <h3 className="font-heading text-lg font-light mb-3" style={{ color: '#F0EDE5' }}>
            Your Referral Link
          </h3>
          <p className="font-body text-xs mb-4" style={{ color: '#9A9A9A' }}>
            Share this link. When someone purchases using your referral, you BOTH get 3 extra entries!
          </p>
          <div className="flex gap-2">
            <div className="flex-1 px-4 py-2.5 text-sm font-body border truncate" style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#666' }}>
              {window.location.origin}?ref={referralInfo?.referral_code || "..."}
            </div>
            <button
              onClick={copyReferralLink}
              className="px-4 py-2.5 border flex items-center gap-2 text-xs font-accent font-semibold transition-colors hover:border-[#FF5A36] hover:text-[#FF5A36]"
              style={{ borderColor: '#2A2A2A', color: '#F0EDE5' }}
              data-testid="copy-referral-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </motion.div>

        {/* Apply Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="p-6 border mb-6"
          style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
        >
          <h3 className="font-heading text-lg font-light mb-3" style={{ color: '#F0EDE5' }}>
            Have a Referral Code?
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter referral code"
              className="flex-1 px-4 py-2.5 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
              style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#F0EDE5' }}
              data-testid="apply-referral-input"
            />
            <button
              onClick={handleApplyReferral}
              disabled={applyingRef || !referralCode.trim()}
              className="px-4 py-2.5 text-xs font-accent font-semibold tracking-wider uppercase text-white transition-all hover:brightness-110 disabled:opacity-40"
              style={{ backgroundColor: '#FF5A36' }}
              data-testid="apply-referral-btn"
            >
              Apply
            </button>
          </div>
        </motion.div>

        {/* Social Media Claim */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 border mb-8"
          style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Camera size={20} style={{ color: '#FF5A36' }} />
            <h3 className="font-heading text-lg font-light" style={{ color: '#F0EDE5' }}>
              Post with #dermasandsoap
            </h3>
          </div>
          <p className="font-body text-xs mb-4" style={{ color: '#9A9A9A' }}>
            Post a pic of your soap on social media with #dermasandsoap, then paste the link below for 2 extra entries! We'll verify manually.
          </p>
          <div className="flex gap-2">
            <input
              type="url"
              value={postLink}
              onChange={(e) => setPostLink(e.target.value)}
              placeholder="Paste your post link here"
              className="flex-1 px-4 py-2.5 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
              style={{ backgroundColor: '#0C0C0C', borderColor: '#2A2A2A', color: '#F0EDE5' }}
              data-testid="social-claim-input"
            />
            <button
              onClick={handleSocialClaim}
              disabled={submitting || !postLink.trim()}
              className="px-4 py-2.5 text-xs font-accent font-semibold tracking-wider uppercase text-white transition-all hover:brightness-110 disabled:opacity-40"
              style={{ backgroundColor: '#FF5A36' }}
              data-testid="social-claim-btn"
            >
              {submitting ? "..." : "Claim"}
            </button>
          </div>
        </motion.div>

        {/* Entry History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <h3 className="font-heading text-xl font-light mb-4" style={{ color: '#F0EDE5' }}>
            Entry History
          </h3>
          {entries.length === 0 ? (
            <div className="p-8 border text-center" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
              <p className="font-body text-sm" style={{ color: '#666' }}>
                No entries yet. Purchase a bar to get started!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map((entry, i) => (
                <div
                  key={entry.id || i}
                  className="flex items-center justify-between p-4 border"
                  style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
                  data-testid={`entry-${i}`}
                >
                  <div>
                    <p className="font-body text-sm font-medium" style={{ color: '#F0EDE5' }}>
                      {entry.type === "purchase" && "Purchase"}
                      {entry.type === "social" && "Social Media Post"}
                      {entry.type === "referral" && "Referral Bonus"}
                      {entry.type === "referral_bonus" && "Referred Signup Bonus"}
                    </p>
                    <p className="font-body text-xs" style={{ color: '#666' }}>
                      {entry.status === "pending_review" ? "Pending review" : "Confirmed"} — {new Date(entry.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="font-heading text-lg" style={{ color: '#FF5A36' }}>
                    +{entry.entries}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const RAFFLE_MAX = 500;
